function formatScaled(
  value: number | null,
  divisor: number,
  unit: string,
  fractionDigits: number,
): string {
  if (value === null) {
    return "--";
  }
  return `${(value / divisor).toFixed(fractionDigits)} ${unit}`;
}

export function formatDeviceId(value: string): string {
  return /^[0-9a-f]{16}$/i.test(value) ? value.toLowerCase() : value;
}

export function formatControllerName(name: string, identifier: string): string {
  return name === `Controller ${identifier}`
    ? `Controller ${formatDeviceId(identifier)}`
    : formatDeviceId(name || identifier);
}

export function formatPercent(value: number | null): string {
  if (value === null) return "--";
  return `${value.toFixed(1)}%`;
}

export function formatPowerKw(value: number | null): string {
  if (value === null) return "--";
  // Backend stores active_power_inst_x10 in tenths of W.
  const watts = value / 10;
  if (Math.abs(watts) >= 1_000_000) return `${(watts / 1_000_000).toFixed(2)} MW`;
  if (Math.abs(watts) >= 1_000) return `${(watts / 1_000).toFixed(2)} kW`;
  return `${watts.toFixed(0)} W`;
}

export function formatPowerWatts(value: number | null): string {
  if (value === null) return "--";
  return `${(value / 10).toFixed(0)} W`;
}

export function formatVoltage(value: number | null): string {
  return formatScaled(value, 100, "V", 1);
}

export function formatCurrent(value: number | null): string {
  return formatScaled(value, 100, "A", 1);
}

export function formatFrequency(value: number | null): string {
  return formatScaled(value, 100, "Hz", 2);
}

export function formatPowerFactor(value: number | null): string {
  if (value === null) return "--";
  return (value / 1000).toFixed(3);
}

export function formatEnergyMwh(value: number | null): string {
  if (value === null) return "--";
  return `${(value / 1_000_000).toFixed(2)} MWh`;
}

export function formatEnergyKwh(value: number | null): string {
  if (value === null) return "--";
  return `${(value / 1_000).toFixed(2)} kWh`;
}

export function formatTimestamp(value: string | null): string {
  if (value === null) return "No telemetry";
  const timestamp = new Date(value);
  if (Number.isNaN(timestamp.getTime())) return value;
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(timestamp);
}

export function formatRelative(value: string | null): string {
  if (value === null) return "never";
  const ts = new Date(value).getTime();
  if (Number.isNaN(ts)) return value;
  const deltaMs = Date.now() - ts;
  const seconds = Math.round(deltaMs / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.round(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.round(months / 12)}y ago`;
}

export function initialsFromName(name: string | null | undefined): string {
  if (!name) return "??";
  return name
    .split(/\s+/)
    .map((part) => part[0] ?? "")
    .join("")
    .slice(0, 2)
    .toUpperCase();
}
